import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Tag,
  Space,
  Card,
  Tooltip,
  Switch,
  Upload,
  Tabs,
  Progress,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ReloadOutlined,
  UploadOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { formatVietnamTime, formatVietnamDate } from '../utils/timeUtils';

function UIDManagement() {
  const [uids, setUids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUID, setEditingUID] = useState(null);
  const [form] = Form.useForm();
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchUIDs, setBatchUIDs] = useState('');
  const [batchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  
  // Loading states
  const [refreshingUIDs, setRefreshingUIDs] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [deletingUIDs, setDeletingUIDs] = useState(new Set());

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchUIDs();
        await fetchUIDStats();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchUIDs = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/uids/?skip=${(page - 1) * pageSize}&limit=${pageSize}`);
      
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated response
        setUids(response.data.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.total
        }));
      } else {
        // Non-paginated response (fallback)
        setUids(response.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.length
        }));
      }
      
      // Automatically clear selection when data changes (pagination)
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to fetch UIDs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUID(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleBatchAdd = () => {
    setBatchUIDs('');
    batchForm.resetFields();
    setBatchModalVisible(true);
  };

  const handleBatchUIDsChange = (e) => {
    setBatchUIDs(e.target.value);
  };

  const handleBatchSubmit = async () => {
    try {
      setBatchSubmitting(true);
      const values = await batchForm.validateFields();
      
      // Parse UIDs from textarea
      const uidList = batchUIDs
        .split('\n')
        .map(uid => uid.trim())
        .filter(uid => uid.length > 0);
      
      if (uidList.length === 0) {
        message.warning('Please enter at least one UID');
        return;
      }

      // Create batch import data
      const batchData = uidList.map(uid => ({
        uid: uid,
        is_active: values.is_active || true
      }));

      // Send batch import request
      const response = await axios.post('/api/uids/batch-import', batchData);
      
      const successCount = response.data?.success ?? 0;
      const failedCount = response.data?.failed ?? 0;
      
      if (successCount > 0) {
        message.success(`Successfully imported ${successCount} UID(s)${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
      } else {
        message.error(`Failed to import all UIDs (${failedCount} failed)`);
      }
      
      setBatchModalVisible(false);
      setBatchUIDs('');
      batchForm.resetFields();
      await fetchUIDs();
      await fetchUIDStats();
      
    } catch (error) {
      console.error('❌ Batch import error:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        message.error(`Import failed: ${error.response.data.detail}`);
      } else {
        message.error('Failed to import UIDs');
      }
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBatchUIDs(content);
    };
    reader.readAsText(file);
    return false; // Prevent default upload
  };

  // UID statistics state
  const [uidStats, setUidStats] = useState({
    activeCount: 0,
    totalRemaining: 0,
    totalCapacity: 0,
    totalUsed: 0,
    usagePercentage: 0
  });

  // Fetch UID statistics from backend (all UIDs, not just current page)
  const fetchUIDStats = async () => {
    try {
      const response = await axios.get('/api/uids/stats');
      setUidStats(response.data);
    } catch (error) {
      console.error('Failed to fetch UID stats:', error);
    }
  };

  const handleEdit = (uid) => {
    setEditingUID(uid);
    form.setFieldsValue({
      uid: uid.uid,
      is_active: uid.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (uidId) => {
    try {
      setDeletingUIDs(prev => new Set(prev).add(uidId));
      await axios.delete(`/api/uids/${uidId}`);
      message.success('UID deleted successfully');
      await fetchUIDs();
      await fetchUIDStats();
    } catch (error) {
      message.error('Failed to delete UID');
    } finally {
      setDeletingUIDs(prev => {
        const next = new Set(prev);
        next.delete(uidId);
        return next;
      });
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select UIDs to delete');
      return;
    }

    try {
      // Delete each UID individually
      const deletePromises = selectedRowKeys.map(uidId => 
        axios.delete(`/api/uids/${uidId}`)
      );
      
      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        message.success(`Successfully deleted ${successful} UID(s)`);
        setSelectedRowKeys([]);
        await fetchUIDs(pagination.current, pagination.pageSize);
        await fetchUIDStats();
      }

      if (failed > 0) {
        message.warning(`${failed} UID(s) failed to delete`);
      }
    } catch (error) {
      console.error('❌ handleBatchDelete error:', error);
      message.error('Failed to delete selected UIDs');
    }
  };

  const handleRefresh = async (uidId) => {
    try {
      setRefreshingUIDs(prev => new Set(prev).add(uidId));
      await axios.post(`/api/uids/${uidId}/refresh`);
      message.success('UID info refreshed successfully');
      await fetchUIDs();
      await fetchUIDStats();
    } catch (error) {
      message.error('Failed to refresh UID info');
    } finally {
      setRefreshingUIDs(prev => {
        const next = new Set(prev);
        next.delete(uidId);
        return next;
      });
    }
  };

  const handleRefreshAll = async () => {
    try {
      setLoading(true);
      const activeUIDs = uids.filter(uid => uid.is_active);
      
      if (activeUIDs.length === 0) {
        message.warning('No active UIDs to refresh');
        return;
      }

      // Refresh all active UIDs in parallel
      const refreshPromises = activeUIDs.map(uid => 
        axios.post(`/api/uids/${uid.id}/refresh`)
      );
      
      const results = await Promise.allSettled(refreshPromises);
      
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      if (successful > 0) {
        message.success(`Successfully refreshed ${successful} UID(s)`);
        await fetchUIDs();
        await fetchUIDStats();
      }
      
      if (failed > 0) {
        message.warning(`${failed} UID(s) failed to refresh`);
      }
      
    } catch (error) {
      message.error('Failed to refresh UIDs');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    // Select all items on the current page
    const currentPageIds = (uids || []).map(u => u.id);
    setSelectedRowKeys(currentPageIds);
  };

  const handleUnselectAll = () => {
    // Deselect all
    setSelectedRowKeys([]);
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      handleSelectAll();
    } else {
      handleUnselectAll();
    }
  };

  // Check if all items on the current page are selected
  const isAllSelected = () => {
    const currentPageIds = (uids || []).map(u => u.id);
    if (currentPageIds.length === 0) return false;
    return currentPageIds.every(id => selectedRowKeys.includes(id));
  };

  // Check if any item is selected (indeterminate state)
  const isIndeterminate = () => {
    const currentPageIds = (uids || []).map(u => u.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRowKeys.includes(id));
    return selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;
  };


  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      if (editingUID) {
        await axios.put(`/api/uids/${editingUID.id}`, values);
        message.success('UID updated successfully');
      } else {
        await axios.post('/api/uids/', values);
        message.success('UID added successfully');
      }
      setModalVisible(false);
      await fetchUIDs();
      await fetchUIDStats();
    } catch (error) {
      message.error('Failed to save UID');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 200,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {text ? `${text.substring(0, 10)}...` : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Tokens',
      key: 'tokens',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Remaining: {record.remaining_tokens?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            Total: {record.total_tokens?.toLocaleString() || 0}
          </div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 80,
      render: (plan) => (
        <Tag color={plan === 'free' ? 'blue' : 'gold'}>
          {plan || 'free'}
        </Tag>
      ),
    },
    {
      title: 'Last Checked',
      dataIndex: 'last_checked',
      key: 'last_checked',
      width: 150,
      render: (date) => date ? formatVietnamTime(date) : 'Never',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => date ? formatVietnamDate(date) : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit UID">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Refresh UID Info">
            <Button 
              type="text" 
              icon={<ReloadOutlined />} 
              onClick={() => handleRefresh(record.id)}
              loading={refreshingUIDs.has(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this UID?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete UID">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                loading={deletingUIDs.has(record.id)}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>UID Management</h2>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>{uidStats.activeCount}</strong> active UIDs
                {uidStats.activeCount > 0 && (
                  <span style={{ marginLeft: '12px', color: '#1890ff' }}>
                    • Total remaining: <strong>{uidStats.totalRemaining.toLocaleString('vi-VN')}</strong> characters
                  </span>
                )}
              </div>
              {uidStats.activeCount > 0 && (
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  <div style={{ marginBottom: '4px' }}>
                    Total capacity: <strong>{uidStats.totalCapacity.toLocaleString('vi-VN')}</strong> characters • 
                    Used: <strong>{uidStats.totalUsed.toLocaleString('vi-VN')}</strong> characters 
                    ({uidStats.usagePercentage}% used)
                  </div>
                  <Progress 
                    percent={uidStats.usagePercentage} 
                    size="small" 
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                    style={{ width: '200px' }}
                  />
                </div>
              )}
            </div>
          </div>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefreshAll}
              loading={loading}
            >
              Check All UIDs
            </Button>
            <Button 
              icon={<FileTextOutlined />} 
              onClick={handleBatchAdd}
            >
              Batch Add UIDs
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd}
            >
              Add UID
            </Button>
          </Space>
        </div>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Checkbox
            checked={isAllSelected()}
            indeterminate={isIndeterminate()}
            onChange={(e) => handleToggleSelectAll(e.target.checked)}
            style={{ marginRight: '16px' }}
          >
            Select All
          </Checkbox>
          <Popconfirm
            title={`Are you sure you want to delete ${selectedRowKeys.length} selected UID(s)?`}
            description="This action cannot be undone."
            onConfirm={handleBatchDelete}
            okText="Yes"
            cancelText="No"
            disabled={selectedRowKeys.length === 0}
          >
            <Button
              icon={<DeleteOutlined />}
              className="action-btn"
              danger
              disabled={selectedRowKeys.length === 0}
              style={{ marginRight: '16px' }}
            >
              Delete ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
          <div style={{ marginRight: '10px', color: '#666' }}>
            Selected: {selectedRowKeys.length}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={uids}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => {
              setSelectedRowKeys(keys);
            },
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} UIDs`,
            pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
              fetchUIDs(page, pageSize);
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
              fetchUIDs(1, size);
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingUID ? 'Edit UID' : 'Add UID'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="uid"
            label="UID"
            rules={[
              { required: true, message: 'Please enter UID' },
              { min: 10, message: 'UID must be at least 10 characters' }
            ]}
          >
            <Input 
              placeholder="Enter UnrealSpeech UID"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>


          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingUID ? 'Update' : 'Add'} UID
              </Button>
              <Button onClick={() => setModalVisible(false)} disabled={submitting}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Batch Import Modal */}
      <Modal
        title="Batch Add UIDs"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchSubmit}
        >
          <Form.Item
            name="is_active"
            label="Status for all UIDs"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item
            label="UIDs (one per line)"
            required
          >
            <Input.TextArea
              value={batchUIDs}
              onChange={handleBatchUIDsChange}
              placeholder="Enter UIDs, one per line:&#10;zQdjrCq71veOdBNmXDtrlVurGH92&#10;CMGevNhyowcj3IjvtfuW6Ui4TFI2&#10;rX0T2wepX0h72XTwja4ahnRIHn22"
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item label="Or upload a text file:">
            <Upload
              accept=".txt"
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>
                Upload TXT File
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={batchSubmitting}>
                Import {batchUIDs.split('\n').filter(uid => uid.trim()).length} UIDs
              </Button>
              <Button onClick={() => setBatchModalVisible(false)} disabled={batchSubmitting}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UIDManagement;